import { computed, ref, onBeforeMount, readonly } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CallTarget, CallTargetCustomField, SalesforceReferenceInfo } from '@/api/openapi';
import { SfCallTargetReferenceService } from '@/api/user/resources/salesforce/sf_call_target_reference';
import { useInputValidations } from '@/composable/input-validations';

type TRule = (validateValue: string | number | null) => string | true;
type TLeadAttribute = {
  node: string;
  title: string;
  value: string;
  rules?: TRule[];
  items?: Record<string, unknown>;
  notification?: number | null;
};
type TLeadAttributeInfo = {
  item: TLeadAttribute | undefined;
  type: 'companyInfo' | 'callTargetName' | 'callTargetInfo';
};
type TCallTargetSfReferenceInfo = Record<string, SalesforceReferenceInfo>;

// -- リード情報（会社・担当者） --

const useLeadAttributes = (getCallTarget: () => CallTarget, getDuplicatedEmailsCount?: () => number) => {
  const i18n = useI18n();
  const { requiredRule, urlRule, phoneRule, emailRule, kanaRule } = useInputValidations();

  const companyInfo = computed(() => getCallTarget().companyInfo);
  const callTargetInfo = computed(() => getCallTarget().callTargetInfo);
  
  const companyAddress = computed(() => {
    return `${companyInfo.value?.companyCountry}${companyInfo.value?.companyPostalCode} ${companyInfo.value?.companyPrefecture}${companyInfo.value?.companyCity}${companyInfo.value?.companyAddressLine}`.trim();
  });

  const companyInfoAttributes = computed<TLeadAttribute[]>(() => {
    return [
      {
        node: 'companyName',
        title: i18n.t('companyInfo.item.companyName'),
        value: companyInfo.value?.companyName,
        rules: [requiredRule],
      },
      {
        node: 'companyUrl',
        title: i18n.t('companyInfo.item.companyUrl'),
        value: companyInfo.value?.companyUrl,
        rules: [urlRule],
      },
      {
        node: 'companyTel_1',
        title: i18n.t('companyInfo.item.companyTel'),
        value: companyInfo.value?.companyTel1,
        rules: [phoneRule],
      },
      {
        node: 'companyAddress',
        title: i18n.t('companyInfo.item.companyAddress'),
        value: companyAddress.value,
        items: {
          companyCountry: companyInfo.value?.companyCountry,
          companyPostalCode: companyInfo.value?.companyPostalCode,
          companyPrefecture: companyInfo.value?.companyPrefecture,
          companyCity: companyInfo.value?.companyCity,
          companyAddressLine: companyInfo.value?.companyAddressLine,
        },
      },
      {
        node: 'numberOfEmployees',
        title: i18n.t('companyInfo.item.numberOfEmployees'),
        value: companyInfo.value?.numberOfEmployees,
      },
      {
        node: 'businessCategory',
        title: i18n.t('companyInfo.item.businessCategory'),
        value: companyInfo.value?.businessCategory,
      },
    ];
  });

  const callTargetNameAttributes = computed<TLeadAttribute[]>(() => {
    return [
      {
        node: 'fullName',
        title: i18n.t('callTargetInfo.item.name'),
        value: `${callTargetInfo.value?.name || '-'} ${callTargetInfo.value?.salutation || ''}`.trim(),
        items: {
          salutation: callTargetInfo.value?.salutation,
          lastName: callTargetInfo.value?.lastName,
          firstName: callTargetInfo.value?.firstName,
        },
      },
      {
        node: 'fullNameKana',
        title: i18n.t('callTargetInfo.item.nameKana'),
        value: callTargetInfo.value?.nameKana,
        items: {
          lastNameKana: callTargetInfo.value?.lastNameKana,
          firstNameKana: callTargetInfo.value?.firstNameKana,
        },
        rules: [kanaRule],
      },
    ];
  });

  const callTargetInfoAttributes = computed<TLeadAttribute[]>(() => {
    return [
      {
        node: 'personalTel',
        title: i18n.t('callTargetInfo.item.personalTel'),
        value: callTargetInfo.value?.personalTel,
        rules: [phoneRule],
      },
      {
        node: 'email',
        title: i18n.t('callTargetInfo.item.email'),
        value: callTargetInfo.value?.email,
        rules: [emailRule],
        notification: getDuplicatedEmailsCount?.() || null,
      },
      {
        node: 'department',
        title: i18n.t('callTargetInfo.item.department'),
        value: callTargetInfo.value?.department,
      },
      {
        node: 'position',
        title: i18n.t('callTargetInfo.item.position'),
        value: callTargetInfo.value?.position,
      },
    ];
  });

  const callTargetInfoAttributesAll = computed<TLeadAttribute[]>(() => {
    return [...callTargetNameAttributes.value, ...callTargetInfoAttributes.value];
  });

  const getAttributeInfoByKey = (key: string): TLeadAttributeInfo | undefined => {
    const [prefix, node] = key.split('.');
    
    if (prefix === 'companyInfo') {
      const item = companyInfoAttributes.value.find(attr => attr.node === node);
      return {
        item,
        type: 'companyInfo',
      };
    }
    
    if (prefix === 'callTargetInfo') {
      const item = callTargetInfoAttributesAll.value.find(attr => attr.node === node);
      const isCallTargetNameAttribute = callTargetNameAttributes.value.some(attr => attr.node === node);
      return {
        item,
        type: isCallTargetNameAttribute ? 'callTargetName' : 'callTargetInfo',
      };
    }
    
    return undefined;
  };

  return {
    companyAddress,
    companyInfoAttributes,
    callTargetNameAttributes,
    callTargetInfoAttributes,
    getAttributeInfoByKey,
  };
};

// -- カスタム項目 --

const useSfReferenceInfo = (getCallTargetId: () => number) => {
  const sfReferenceInfo = ref<TCallTargetSfReferenceInfo | null>(null);

  onBeforeMount(async () => {
    const apiService = new SfCallTargetReferenceService();
    const value = await apiService.getCallTargetSfReferenceInfo({ callTargetId: getCallTargetId() });
    sfReferenceInfo.value = value.data;
  });

  return {
    sfReferenceInfo: readonly(sfReferenceInfo),
  };
};

const useCustomFieldAttributes = (
  getSfReferenceInfo: () => TCallTargetSfReferenceInfo | null,
) => {
  const getCustomFieldValue = (field: CallTargetCustomField) => {
    if (field.dataType !== 'sf_reference') return field.value;
    const sfReferenceInfo = getSfReferenceInfo();
    if (sfReferenceInfo == null) return 'loading';

    const fieldValue = sfReferenceInfo[`custom_field_${field.id}`];
    if (fieldValue == null) return '未連携';
    return fieldValue.name as string;
  };

  const getCustomFieldSfLinkValue = (field: CallTargetCustomField) => {
    if (field.dataType !== 'sf_reference') return null;
    const sfReferenceInfo = getSfReferenceInfo();
    if (sfReferenceInfo == null) return null;

    const fieldValue = sfReferenceInfo[`custom_field_${field.id}`];
    if (fieldValue == null) return null;
    return fieldValue.sf_link;
  };

  return {
    getCustomFieldValue,
    getCustomFieldSfLinkValue,
  };
};

export type {
  TLeadAttributeInfo,
};
export {
  useLeadAttributes,
  useSfReferenceInfo,
  useCustomFieldAttributes,
};
